<template>
  <Dropdown
    v-if="useDropdown"
    class="tree-field"
    :closed-text="decodeHTML(text)"
  >
    <Tree
      ref="tree"
      v-model="treeValue"
      :items="items"
      :children-key="childrenKey"
      :name-key="nameKey"
      :multiple="multiple"
    />
  </Dropdown>

  <div v-else class="editable-label tree-field" @click="edit">
    <div class="legend legend--bottom-margin">{{ legend }}</div>
    <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
    <div class="editable-label-text" :placeholder="placeholder">{{ decodeHTML(text) }}</div>
    <transition name="fade" mode="out-in">
      <div v-if="editing" class="dark-overlay v-centerer" @click.stop="close">
        <div class="dark-overlay-content large" @click.stop="">
          <div class="tree-field__modal-content">
            <div class="tree-field__modal-content__title">
              <h2 class="basic-sub-title">{{ legend }}</h2>
              <CloseButton absolute @click.native.stop="close" />
            </div>
            <div class="tree-field__modal-content__tree">
              <Tree
                ref="tree"
                v-model="treeValue"
                :items="items"
                :children-key="childrenKey"
                :name-key="nameKey"
                :multiple="multiple"
              />
            </div>
            <div class="h-v-centerer tree-field__modal-content__buttons">
              <button class="generic-border generic-border--blue generic-border--text-btn" @click.stop="itemPicked">
                {{ $t("message.OK") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Field editable through a Tree component
 *
 * @vue-prop {Number|Array} value - If props.multiple is true, this should be an array of ids.
 *                                  Otherwise, id of the selected item
 * @vue-prop {String} legend - legend of this field (as in EditableLabel)
 * @vue-prop {String} placeholder - Placeholder when no value selected
 * @vue-prop {Array} items - All selectable items in a tree-like format :
 *                           { id: 1, nameKey: 'Name', childrenKey: [{...}] }
 * @vue-prop {String} [childrenKey='zones'] - Key to use for the children of each tree leaf (see above)
 * @vue-prop {String} [nameKey='name'] - Key to use as the name (title) of each leaf (see above)
 * @vue-prop {Boolean} disabled - Prevents edition
 * @vue-prop {Boolean} multiple - True to allow selecting multiple items
 */

// Components
import CloseButton from '@/components/close-button.vue';
import Dropdown from '@/components/dropdown.vue';
import Tree from '@/components/tree.vue';
// Utils
import { decodeHTML, nestedFindItemWithId, treeIdsText } from '@/utils/main';

export default {

  name: 'TreeField',

  components: {
    CloseButton,
    Dropdown,
    Tree,
  },

  props: {
    value: {
      required: true,
      validator: val => typeof val === 'number' || val instanceof Array || val === null,
    },
    legend: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    childrenKey: {
      type: String,
      default: 'zones',
    },
    nameKey: {
      type: String,
      default: 'name',
    },
    disabled: Boolean,
    multiple: Boolean,
    useDropdown: Boolean,
  },

  data() {
    return {
      editing: false,
      idSelected: 0,
    };
  },

  computed: {
    /** String: Text displayed as the current value when the field isn't being edited */
    text() {
      if (this.multiple) {
        return treeIdsText(this.items, this.value, this.nameKey, this.childrenKey, this.$t('message.NO_SELECTION'));
      }
      if (this.treeValue && typeof this.treeValue === 'object') {
        return this.treeValue[this.nameKey];
      }
      return '';
    },
    /** Number|Array: Current value of the tree component */
    treeValue: {
      get() {
        return this.multiple
          ? this.value
          : nestedFindItemWithId(this.items, this.value, this.childrenKey);
      },
      set(newVal) {
        if (this.multiple) {
          this.$emit('input', newVal);
        } else {
          this.$emit('input', newVal ? newVal.id : 0);
        }
      },
    },
  },

  methods: {
    decodeHTML,
    edit() {
      if (!this.disabled) {
        this.editing = true;
      }
    },
    itemPicked() {
      this.editing = false;
      this.$emit('input', this.value);
      this.$emit('okClick');
    },
    close() {
      this.editing = false;
      if (this.value !== null && this.value !== undefined && this.value.id > 0) {
        this.$emit('input', this.value);
      } else {
        this.$emit('close');
      }
    },
  },
};
</script>

<style lang="scss">
.tree-field {

  > .legend, > .editable-label-text {
    cursor: pointer;
  }

  > .dark-overlay .dark-overlay-content.large {
    padding: 1rem 0;

    h2 {
      margin-top: 0px;
    }
  }

  &__modal-content {
    height: 100%;
    display: flex;
    flex-flow: column;

    &__tree {
      flex: 1;
      min-height: 0px;
      position: relative;
      display: flex;
      flex-flow: column;

      .tree {
        overflow: auto;
        flex: 1;
        min-height: 0px;
      }

      &:after {
        content: " ";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2.5rem;
        box-shadow: inset 0 -8px 8px white;
        pointer-events: none;
      }
    }

    &__title {
      flex: 0 0 auto;
      margin-left: 1rem;
    }

    &__buttons {
      flex: 0 0 auto;
    }
  }
}
</style>
